import React from 'react';
import Stack from 'atoms/Layout/Stack/Stack';
import FeaturedIconListsWithTextBlock from 'molecules/FeaturedIconListsWithTextBlock/FeaturedIconListsWithTextBlock';
import { HeadingAndSupportingTextProps } from 'molecules/HeadingAndSupportingText/HeadingAndSupportingText';
import { FeaturedIconListProps } from 'molecules/FeaturedIconList/FeaturedIconList';
import { TypographyProps } from 'atoms/Typography/Typography';
import GraduationHat from 'app/icons/Monospaced/GraduationHat/GraduationHat';
import { ButtonProps } from 'atoms/Button/Button';
import Arrow from 'app/icons/Monospaced/Arrow/Arrow';
import MessageChatCircle from 'app/icons/Monospaced/MessageChat/MessageChatCircle';
import VideoRecorder from 'app/icons/Monospaced/VideoRecorder/VideoRecorder';
import Award03 from 'app/icons/Monospaced/Award/Award03';
import Repeat03 from 'app/icons/Monospaced/Repeat/Repeat03';
import Star06 from 'app/icons/Monospaced/Star/Star06';
import CurrencyDollar from 'app/icons/Monospaced/Currency/CurrencyDollar';
import Settings01 from 'app/icons/Monospaced/Settings/Settings01';
import Users01 from 'app/icons/Monospaced/Users/Users01';
import SearchRefraction from 'app/icons/Monospaced/SearchRefraction/SearchRefraction';
import UserCircle from 'app/icons/Monospaced/User/UserCircle';

const FeaturesListsWithIconCards = (): JSX.Element => {
  const headingAndSupportingText: HeadingAndSupportingTextProps = {
    heading: {
      type: 'heading',
      size: 'xl',
      textAlign: 'left',
      text: 'Build a community so impactful that growth comes naturally',
    },
    supportingText: {
      type: 'paragraph',
      size: 'xl',
      color: 'text-tertiary',
      textAlign: 'left',
      text: 'Our features turn members into momentum.',
    },
  };

  const featuredIconListTitleProps = (text: string): TypographyProps => ({
    type: 'heading',
    size: 'sm',
    textAlign: 'left',
    fontWeight: 'font-weight-semibold',
    text,
  });

  const featuredIconTitleProps = (text: string): TypographyProps => ({
    type: 'paragraph',
    size: 'lg',
    textAlign: 'left',
    fontWeight: 'font-weight-semibold',
    text,
  });

  const featuredIconSupportingTextProps = (text: string): TypographyProps => ({
    type: 'paragraph',
    size: 'md',
    textAlign: 'left',
    color: 'text-tertiary',
    text,
  });

  const featuredIconButtonProps = (href: string): ButtonProps => ({
    variant: 'brand-text-link',
    icon: (
      <Arrow
        color='var(--brand-500)'
        width={20}
        orientation='right'
      />
    ),
    iconPosition: 'right',
    size: 'md',
    children: 'Learn more',
    href,
  });

  const memberExperiencesFeaturedIconList: FeaturedIconListProps = {
    title: featuredIconListTitleProps('Member Experiences'),
    featuredIconList: [
      {
        heading: featuredIconTitleProps('Courses & Challenges'),
        supportingText: featuredIconSupportingTextProps(
          'Send your members on quests so they level up faster, together.'
        ),
        buttonProps: featuredIconButtonProps('/product/online-courses'),
        featuredIcon: {
          icon: <GraduationHat width={24} />,
        },
      },
      {
        heading: featuredIconTitleProps('Dynamic Discussions'),
        supportingText: featuredIconSupportingTextProps(
          'Banter, brainstorms, deep-dive threads. Light up the chats on every level of your network. 🔥'
        ),
        buttonProps: featuredIconButtonProps('/product/chat'),
        featuredIcon: {
          icon: <MessageChatCircle width={24} />,
        },
      },
      {
        heading: featuredIconTitleProps('Livestreaming'),
        supportingText: featuredIconSupportingTextProps(
          'Built right in, with native notifications to get people in and engaged.'
        ),
        buttonProps: featuredIconButtonProps('/product/events-livestreaming'),
        featuredIcon: {
          icon: <VideoRecorder width={24} />,
        },
      },
      {
        heading: featuredIconTitleProps('Next-Level Gamification'),
        supportingText: featuredIconSupportingTextProps(
          'Celebrate what actually counts and build momentum every day, for every member.'
        ),
        buttonProps: featuredIconButtonProps('/product/gamification'),
        featuredIcon: {
          icon: <Award03 width={24} />,
        },
      },
    ],
  };

  const lessAdminMoreGrowthFeaturedIconList: FeaturedIconListProps = {
    title: featuredIconListTitleProps('Less Admin, More Growth'),
    featuredIconList: [
      {
        heading: featuredIconTitleProps('Unlimited Automations'),
        supportingText: featuredIconSupportingTextProps(
          'Scale up—without losing momentum, impact, or your personal touch.'
        ),
        buttonProps: featuredIconButtonProps('/product/automations'),
        featuredIcon: {
          icon: <Repeat03 width={24} />,
        },
      },
      {
        heading: featuredIconTitleProps('AI Cohost'),
        supportingText: featuredIconSupportingTextProps(
          'Meet your best teammate, a built-in, 24/7 strategist trained on features and community.'
        ),
        buttonProps: featuredIconButtonProps('/product/ai'),
        featuredIcon: {
          icon: <Star06 width={24} />,
        },
      },
      {
        heading: featuredIconTitleProps('Streamlined Payments'),
        supportingText: featuredIconSupportingTextProps(
          'Easily sell a single course, bundles, subscriptions, high-ticket offers, and more.'
        ),
        buttonProps: featuredIconButtonProps('/product/payments'),
        featuredIcon: {
          icon: <CurrencyDollar width={24} />,
        },
      },
      {
        heading: featuredIconTitleProps('Smart Integrations'),
        supportingText: featuredIconSupportingTextProps(
          'Keep what you like. Includes native Zoom, an exclusive Kit connection & tracking codes.'
        ),
        buttonProps: featuredIconButtonProps('/product/embeds-integrations'),
        featuredIcon: {
          icon: <Settings01 width={24} />,
        },
      },
    ],
  };

  const peopleMagicFeaturedIconList: FeaturedIconListProps = {
    title: featuredIconListTitleProps('People Magic'),
    featuredIconList: [
      {
        heading: featuredIconTitleProps('Show Similarities'),
        supportingText: featuredIconSupportingTextProps(
          'Our AI does things differently, so your members can make the best friends of their lives.'
        ),
        buttonProps: featuredIconButtonProps('/product/ai'),
        featuredIcon: {
          icon: <Users01 width={24} />,
        },
      },
      {
        heading: featuredIconTitleProps('People Explorer'),
        supportingText: featuredIconSupportingTextProps(
          'No dead ends here. We make it endlessly fascinating to discover who else is out there.'
        ),
        buttonProps: featuredIconButtonProps('/product/ai'),
        featuredIcon: {
          icon: <SearchRefraction width={24} />,
        },
      },
      {
        heading: featuredIconTitleProps('Conversation Starters'),
        supportingText: featuredIconSupportingTextProps(
          'No awkward introductions either. Our suggestions make the connections feel natural.'
        ),
        buttonProps: featuredIconButtonProps('/product/ai'),
        featuredIcon: {
          icon: <MessageChatCircle width={24} />,
        },
      },
      {
        heading: featuredIconTitleProps('Profiles That Work Harder'),
        supportingText: featuredIconSupportingTextProps(
          'You get insights. They get experiences and connections based on their interests.'
        ),
        buttonProps: featuredIconButtonProps('/product/ai'),
        featuredIcon: {
          icon: <UserCircle width={24} />,
        },
      },
    ],
  };

  return (
    <Stack
      padding={['spacing-none', 'spacing-4xl']}
      paddingSm={['spacing-none', 'spacing-xl']}
    >
      <FeaturedIconListsWithTextBlock
        headingAndSupportingText={headingAndSupportingText}
        featuredIconLists={[
          memberExperiencesFeaturedIconList,
          lessAdminMoreGrowthFeaturedIconList,
          peopleMagicFeaturedIconList,
        ]}
      />
    </Stack>
  );
};

export default FeaturesListsWithIconCards;
